// © Microsoft Corporation. All rights reserved.

import { DeleteMenuTelemetryObjects, deleteMenuTelemetryActionDispatchers, deleteMenuTelemetryContracts } from "./DeleteMenu.telemetry";
import Popover, { PopoverProps } from "../../common/popover/Popover";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";

import AccessibilityHelpers from "../../utils/AccessibilityHelpers";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";

export interface DeleteMenuProps extends PopoverProps {
    id: string;
    managedClasses?: SocialClassNameContract;
    onConfirmDelete?: (any) => void;
    t: SocialLocalizedStrings;
    mainTelemetryObject?: TelemetryObjectBase;
}

interface DeleteMenuState {
    visible: boolean
}

export default class DeleteMenu extends React.Component<DeleteMenuProps, DeleteMenuState> {
    telemetryObjects: DeleteMenuTelemetryObjects;
    containerRef: HTMLDivElement;

    constructor(props) {
        super(props);

        this.state = { visible: true };
        this.containerRef = null;
    }

    componentDidMount() {
        AccessibilityHelpers.attachAccessibilityListeners(document);
    }

    cancelHandler(e) {
        e.stopPropagation();
        e.preventDefault();
        this.props.onHide();
        deleteMenuTelemetryActionDispatchers.genericClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.cancelButton);
        const ref = window.document.getElementById(`deleteMenu_${this.props.id}`);
        if (ref) {
            window.setTimeout(() => { ref.focus(); }, 50);
        }
    }

    yesHandler(e) {
        deleteMenuTelemetryActionDispatchers.genericClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.yesButton);
        this.props.onConfirmDelete(e);
    }

    handleKeyDown(e) {
        if (e.keyCode == 27) { /* ESC key */
            const ref = window.document.getElementById(`deleteMenu_${this.props.id}`);
            if (ref) {
                window.setTimeout(() => { ref.focus(); }, 50);
            }
            e.preventDefault();
            e.stopPropagation();
            this.props.onHide();
        } else if (e.keyCode == 38) { /* Up arrow */
            e.preventDefault();
            e.stopPropagation();
            const prev = e.target.previousElementSibling;
            if (prev) {
                prev.focus();
            } else {
                e.target.parentElement.lastElementChild.focus();
            }
        } else if (e.keyCode == 40) { /* Down arrow */
            e.preventDefault();
            e.stopPropagation();
            const next = e.target.nextElementSibling;
            if (next) {
                next.focus();
            } else {
                e.target.parentElement.firstElementChild.focus();
            }
        }
    }

    render() {
        const { managedClasses, t, id, mainTelemetryObject } = this.props;

        /** Generate Telemetry Objects */
        if (mainTelemetryObject) {
            this.telemetryObjects = {
                yesButton: addOrUpdateTelemetryObject(mainTelemetryObject, deleteMenuTelemetryContracts.yesButton, { id }, this.telemetryObjects && this.telemetryObjects.yesButton),
                cancelButton: addOrUpdateTelemetryObject(mainTelemetryObject, deleteMenuTelemetryContracts.cancelButton, { id }, this.telemetryObjects && this.telemetryObjects.cancelButton),
                deleteMenu: addOrUpdateTelemetryObject(mainTelemetryObject, deleteMenuTelemetryContracts.deleteMenu, { id }, this.telemetryObjects && this.telemetryObjects.deleteMenu)
            };
        }

        return (
            <Popover {... {...this.props,  hasFocusLock: true} }>
                <div className={managedClasses.deleteMenuContainer} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.deleteMenu, true, (el) => { this.containerRef = el; })}
                    onKeyDown={(e) => { this.handleKeyDown(e); }}>
                    <i className="cwt-icon cwt-menu-header">
                        <span className="cwt-icon-label">  {t.DeleteMenuDeleteComment}</span>
                    </i>
                    <ul className="cwt-delete-list" role="listbox" aria-label={t.AriaLabelsOpenDeleteCommentMenu}>
                        <li className="cwt-link" role="option" onClick={e => this.yesHandler(e)} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.yesButton, false)}>
                            {t.DeleteMenuDeleteCommentConfirm}
                        </li>
                        <li className="cwt-link" role="option" onClick={e => this.cancelHandler(e)} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.cancelButton, false)}>
                            {t.DeleteMenuDeleteCommentCancel}
                        </li>
                    </ul>
                </div>
            </Popover>
        );
    }
}